'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  createChart,
  AreaSeries,
  HistogramSeries,
  ColorType,
  CrosshairMode,
  type IChartApi,
  type ISeriesApi,
  type Time,
} from 'lightweight-charts';
import { formatUSD, formatUSDCompact, formatTokenCompact, cn } from '@/lib/utils';
import { getTaxTokenConfig } from '@/lib/drawer-utils';
import type { V2FloorPriceSnapshot } from '@/lib/types/v2';
import type { SeatData } from '@/components/grid';

export interface SaleAnnotation {
  timestamp: number;
  price: number;
  type: 'sale' | 'yield';
  label?: string;
  buyerName?: string;
  buyerAvatar?: string;
  sellerName?: string;
  sellerAvatar?: string;
}

export type TimeRange = '24h' | '7d' | '1m' | 'all';

const RANGE_SECONDS: Record<TimeRange, number | null> = {
  '24h': 24 * 60 * 60,
  '7d': 7 * 24 * 60 * 60,
  '1m': 30 * 24 * 60 * 60,
  'all': null,
};

const RANGE_LABELS: { key: TimeRange; label: string }[] = [
  { key: '24h', label: '24h' },
  { key: '7d', label: '7D' },
  { key: '1m', label: '1M' },
  { key: 'all', label: 'ALL' },
];

function dedupByTime(data: { time: number; value: number }[]) {
  const map = new Map<number, { time: number; value: number }>();
  for (const d of data) map.set(d.time, d);
  return Array.from(map.values());
}

function formatTime(ts: number): string {
  const d = new Date(ts * 1000);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Tile Sales Chart
// ---------------------------------------------------------------------------

interface SeatSalesChartProps {
  snapshots: V2FloorPriceSnapshot[];
  saleAnnotations?: SaleAnnotation[];
  range?: TimeRange;
  onRangeChange?: (r: TimeRange) => void;
  /** Same wiring as `EventFeed` — volume header + crosshair use TAKEOVER vs USD correctly. */
  taxToken?: string;
  taxTokenDecimalsIndexer?: number;
  /** Resolved grid decimals when indexer field is missing (lobby / loading). */
  taxTokenDecimals?: number;
  takeoverPriceUsd?: number;
}

export function SeatSalesChart({
  snapshots,
  saleAnnotations = [],
  range: externalRange,
  onRangeChange,
  taxToken,
  taxTokenDecimalsIndexer,
  taxTokenDecimals,
  takeoverPriceUsd = 0,
}: SeatSalesChartProps) {
  const [internalRange, setInternalRange] = useState<TimeRange>('all');
  const range = externalRange ?? internalRange;
  const setRange = onRangeChange ?? setInternalRange;
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Area'> | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const { chartData, periodVolume } = useMemo(() => {
    const sales = saleAnnotations.filter(a => a.type === 'sale').sort((a, b) => a.timestamp - b.timestamp);
    if (!sales.length) return { chartData: [] as { time: number; value: number }[], periodVolume: 0 };

    const cutoff = RANGE_SECONDS[range];
    const now = sales[sales.length - 1].timestamp;
    const filtered = cutoff ? sales.filter(s => s.timestamp >= now - cutoff) : sales;

    let vol = 0;
    for (const ann of filtered) vol += ann.price;

    let cd = dedupByTime(filtered.map(s => ({
      time: s.timestamp as unknown as number,
      value: s.price,
    })));

    if (cd.length === 1) {
      cd = [{ time: (cd[0].time - 60) as unknown as number, value: cd[0].value }, cd[0]];
    }

    return { chartData: cd, periodVolume: vol };
  }, [saleAnnotations, range]);

  const volumeMetricStr = useMemo(() => {
    const ttc = getTaxTokenConfig(taxToken, undefined, taxTokenDecimalsIndexer ?? taxTokenDecimals);
    if (ttc.isTakeover && takeoverPriceUsd > 0) return formatUSDCompact(periodVolume * takeoverPriceUsd);
    if (ttc.isTakeover) return formatTokenCompact(periodVolume, ttc.symbol);
    return formatUSDCompact(periodVolume);
  }, [taxToken, taxTokenDecimalsIndexer, taxTokenDecimals, takeoverPriceUsd, periodVolume]);

  const handleCrosshairMove = useCallback((param: any) => {
    const tooltip = tooltipRef.current;
    const container = containerRef.current;
    if (!tooltip || !container) return;

    if (!param.point || !param.time || param.point.x < 0 || param.point.y < 0) {
      tooltip.style.display = 'none';
      return;
    }

    const ts = param.time as number;
    const series = seriesRef.current;
    const data = series ? param.seriesData.get(series) : null;
    const floor = data?.value as number | undefined;

    if (floor == null) { tooltip.style.display = 'none'; return; }

    const ttc = getTaxTokenConfig(taxToken, undefined, taxTokenDecimalsIndexer ?? taxTokenDecimals);
    let priceStr: string;
    if (ttc.isTakeover && takeoverPriceUsd > 0) priceStr = formatUSD(floor * takeoverPriceUsd);
    else if (ttc.isTakeover) priceStr = `${formatTokenCompact(floor)} ${ttc.symbol}`;
    else priceStr = formatUSD(floor);

    tooltip.innerHTML = `
      <div class="text-[10px] text-muted-foreground font-mono">${formatTime(ts)}</div>
      <div class="flex items-center justify-between gap-4">
        <span class="flex items-center gap-1.5">
          <span class="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
          <span class="text-muted-foreground text-[11px]">Sale price</span>
        </span>
        <span class="font-mono text-[11px]">${priceStr}</span>
      </div>`;
    tooltip.style.display = 'block';

    const rect = container.getBoundingClientRect();
    const px = rect.left + param.point.x;
    const py = rect.top + param.point.y;
    const tw = tooltip.clientWidth;
    const th = tooltip.clientHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = px + 16 + tw > vw ? px - tw - 12 : px + 16;
    const top = py - 20 + th > vh ? py - th - 8 : Math.max(4, py - 20);
    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  }, [taxToken, taxTokenDecimalsIndexer, taxTokenDecimals, takeoverPriceUsd]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const chart = createChart(container, {
      layout: { background: { type: ColorType.Solid, color: 'transparent' }, textColor: '#888', attributionLogo: false },
      grid: { vertLines: { visible: false }, horzLines: { color: 'rgba(255,255,255,0.03)' } },
      crosshair: {
        mode: CrosshairMode.Magnet,
        vertLine: { color: 'rgba(255,255,255,0.15)', width: 1, style: 3, labelVisible: false },
        horzLine: { visible: false, labelVisible: false },
      },
      rightPriceScale: { visible: false },
      timeScale: { visible: false, borderVisible: false },
      handleScroll: false,
      handleScale: false,
      width: container.clientWidth,
      height: container.clientHeight,
    });

    const series = chart.addSeries(AreaSeries, {
      lineColor: '#60a5fa',
      topColor: 'rgba(96,165,250,0.2)',
      bottomColor: 'rgba(96,165,250,0)',
      lineWidth: 2,
      crosshairMarkerRadius: 3,
      crosshairMarkerBorderColor: '#2563eb',
      crosshairMarkerBackgroundColor: '#fff',
      crosshairMarkerBorderWidth: 1.5,
    });

    chartRef.current = chart;
    seriesRef.current = series;

    const ro = new ResizeObserver(() => {
      chart.resize(container.clientWidth, container.clientHeight);
    });
    ro.observe(container);

    chart.subscribeCrosshairMove(handleCrosshairMove);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, [handleCrosshairMove]);

  useEffect(() => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    if (!series || !chart) return;

    if (chartData.length > 0) {
      try {
        series.setData(chartData as any);
        chart.timeScale().fitContent();
      } catch (e) {
        console.warn('[SeatSalesChart] setData failed:', e);
      }
    }
  }, [chartData]);

  if (chartData.length < 2) {
    return (
      <div className="space-y-2">
        <ChartHeader metric={volumeMetricStr} metricLabel="volume" ranges={RANGE_LABELS} range={range} setRange={setRange} />
        <div className="flex items-center justify-center h-44 text-muted-foreground text-sm">
          Not enough data for this range
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <ChartHeader metric={volumeMetricStr} metricLabel="volume" ranges={RANGE_LABELS} range={range} setRange={setRange} />
      <div className="h-44 -mx-1">
        <div ref={containerRef} className="w-full h-full" />
        {/*
          Portal the tooltip to <body> so its `position: fixed` is
          measured against the viewport — when this chart renders
          inside the lobby's `DraggableFloatingPanel`, the parent
          `motion.div` always carries a `transform`, which would
          otherwise turn `fixed` into "fixed-relative-to-the-panel"
          and the tooltip lands off-screen at the recorded
          `getBoundingClientRect()` coords. Same Tailwind classes,
          same positioning math — only the mount point changes.
        */}
        {typeof document !== 'undefined' && createPortal(
          <div
            ref={tooltipRef}
            className="fixed z-[9999] pointer-events-none rounded-lg border bg-card/95 backdrop-blur-sm p-2.5 shadow-lg text-sm space-y-1.5 min-w-[160px]"
            style={{ display: 'none', top: 0, left: 0 }}
          />,
          document.body,
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rewards Chart
// ---------------------------------------------------------------------------

interface RewardsChartProps {
  snapshots: V2FloorPriceSnapshot[];
  saleAnnotations?: SaleAnnotation[];
  yieldTokenSymbol?: string;
}

export function RewardsChart({ snapshots, saleAnnotations = [], yieldTokenSymbol = 'ETH' }: RewardsChartProps) {
  const [range, setRange] = useState<TimeRange>('all');
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Area'> | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const { chartData, periodRewards } = useMemo(() => {
    if (!snapshots.length) return { chartData: [] as { time: number; value: number }[], periodRewards: 0 };

    const cutoff = RANGE_SECONDS[range];
    const now = Math.max(...snapshots.map(s => s.timestamp));
    const filtered = cutoff ? snapshots.filter(s => s.timestamp >= now - cutoff) : snapshots;

    const yieldEvents = saleAnnotations.filter(a => a.type === 'yield');
    const filteredYields = cutoff ? yieldEvents.filter(y => y.timestamp >= now - cutoff) : yieldEvents;
    const totalRewards = filteredYields.reduce((sum, y) => sum + y.price, 0);

    const cd = dedupByTime(filtered.map(s => {
      const yieldsUpTo = yieldEvents.filter(y => y.timestamp <= s.timestamp);
      const cumulative = yieldsUpTo.reduce((sum, y) => sum + y.price, 0);
      return { time: s.timestamp as unknown as number, value: cumulative };
    }));

    return { chartData: cd, periodRewards: totalRewards };
  }, [snapshots, saleAnnotations, range]);

  const handleCrosshairMove = useCallback((param: any) => {
    const tooltip = tooltipRef.current;
    const container = containerRef.current;
    if (!tooltip || !container) return;

    if (!param.point || !param.time || param.point.x < 0 || param.point.y < 0) {
      tooltip.style.display = 'none';
      return;
    }

    const ts = param.time as number;
    const series = seriesRef.current;
    const data = series ? param.seriesData.get(series) : null;
    const val = data?.value as number | undefined;

    if (val == null) { tooltip.style.display = 'none'; return; }

    tooltip.innerHTML = `
      <div class="text-[10px] text-muted-foreground font-mono">${formatTime(ts)}</div>
      <div class="flex items-center justify-between gap-4">
        <span class="flex items-center gap-1.5">
          <span class="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
          <span class="text-muted-foreground text-[11px]">Rewards</span>
        </span>
        <span class="font-mono text-emerald-400 text-[11px]">${val.toFixed(4)} ${yieldTokenSymbol}</span>
      </div>`;
    tooltip.style.display = 'block';

    const rect = container.getBoundingClientRect();
    const px = rect.left + param.point.x;
    const py = rect.top + param.point.y;
    const tw = tooltip.clientWidth;
    const th = tooltip.clientHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = px + 16 + tw > vw ? px - tw - 12 : px + 16;
    const top = py - 20 + th > vh ? py - th - 8 : Math.max(4, py - 20);
    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const chart = createChart(container, {
      layout: { background: { type: ColorType.Solid, color: 'transparent' }, textColor: '#888', attributionLogo: false },
      grid: { vertLines: { visible: false }, horzLines: { color: 'rgba(255,255,255,0.03)' } },
      crosshair: {
        mode: CrosshairMode.Magnet,
        vertLine: { color: 'rgba(255,255,255,0.15)', width: 1, style: 3, labelVisible: false },
        horzLine: { visible: false, labelVisible: false },
      },
      rightPriceScale: { visible: false },
      timeScale: { visible: false, borderVisible: false },
      handleScroll: false,
      handleScale: false,
      width: container.clientWidth,
      height: container.clientHeight,
    });

    const series = chart.addSeries(AreaSeries, {
      lineColor: '#34d399',
      topColor: 'rgba(52,211,153,0.2)',
      bottomColor: 'rgba(52,211,153,0)',
      lineWidth: 2,
      crosshairMarkerRadius: 3,
      crosshairMarkerBorderColor: '#059669',
      crosshairMarkerBackgroundColor: '#fff',
      crosshairMarkerBorderWidth: 1.5,
    });

    chartRef.current = chart;
    seriesRef.current = series;

    const ro = new ResizeObserver(() => {
      chart.resize(container.clientWidth, container.clientHeight);
    });
    ro.observe(container);

    chart.subscribeCrosshairMove(handleCrosshairMove);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, [handleCrosshairMove]);

  useEffect(() => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    if (!series || !chart) return;

    if (chartData.length > 0) {
      try {
        series.setData(chartData as any);
        chart.timeScale().fitContent();
      } catch (e) {
        console.warn('[RewardsChart] setData failed:', e);
      }
    }
  }, [chartData]);

  if (chartData.length < 2) {
    return (
      <div className="space-y-2">
        <ChartHeader metric={`${periodRewards.toFixed(4)} ${yieldTokenSymbol}`} metricLabel="earned" ranges={RANGE_LABELS} range={range} setRange={setRange} color="emerald" />
        <div className="flex items-center justify-center h-44 text-muted-foreground text-sm">
          Not enough data for this range
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <ChartHeader metric={`${periodRewards.toFixed(4)} ${yieldTokenSymbol}`} metricLabel="earned" ranges={RANGE_LABELS} range={range} setRange={setRange} color="emerald" />
      <div className="h-44 -mx-1">
        <div ref={containerRef} className="w-full h-full" />
        {/* Portal — see the SeatSalesChart tooltip comment above. */}
        {typeof document !== 'undefined' && createPortal(
          <div
            ref={tooltipRef}
            className="fixed z-[9999] pointer-events-none rounded-lg border bg-card/95 backdrop-blur-sm p-2.5 shadow-lg text-sm space-y-1.5 min-w-[140px]"
            style={{ display: 'none', top: 0, left: 0 }}
          />,
          document.body,
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Listings (Depth) Chart
// ---------------------------------------------------------------------------

interface ListingsChartProps {
  seats: SeatData[];
  taxTokenDecimals?: number;
  tokenPriceUsd?: number;
  taxTokenSymbol?: string;
}

export function ListingsChart({ seats, taxTokenDecimals = 6, tokenPriceUsd = 1, taxTokenSymbol }: ListingsChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const showTokenHint = taxTokenSymbol && tokenPriceUsd !== 1;

  const { buckets, avgPrice } = useMemo(() => {
    const listed = seats.filter(s => !!s.holder && !s.isForfeited);
    if (listed.length === 0) return { buckets: [] as { tokenPrice: number; usdPrice: number; count: number; cumulative: number }[], avgPrice: 0 };

    const divisor = 10 ** taxTokenDecimals;
    const prices = listed.map(s => {
      const token = Number(s.selfAssessedPrice) / divisor;
      return { token, usd: token * tokenPriceUsd };
    }).sort((a, b) => a.usd - b.usd);
    const avg = prices.reduce((sum, p) => sum + p.usd, 0) / prices.length;

    const priceMap = new Map<string, { tokenPrice: number; usdPrice: number; count: number }>();
    for (const p of prices) {
      const key = (Math.round(p.usd * 100) / 100).toFixed(2);
      const existing = priceMap.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        priceMap.set(key, { tokenPrice: p.token, usdPrice: Math.round(p.usd * 100) / 100, count: 1 });
      }
    }

    let cumulative = 0;
    const result = Array.from(priceMap.values())
      .sort((a, b) => a.usdPrice - b.usdPrice)
      .map(({ tokenPrice, usdPrice, count }) => {
        cumulative += count;
        return { tokenPrice, usdPrice, count, cumulative };
      });

    return { buckets: result, avgPrice: avg };
  }, [seats, taxTokenDecimals, tokenPriceUsd]);

  const handleCrosshairMove = useCallback((param: any) => {
    const tooltip = tooltipRef.current;
    const container = containerRef.current;
    if (!tooltip || !container) return;

    if (!param.point || !param.time || param.point.x < 0 || param.point.y < 0) {
      tooltip.style.display = 'none';
      return;
    }

    const idx = (param.time as number) - 1;
    const bucket = buckets[idx];
    if (!bucket) { tooltip.style.display = 'none'; return; }

    const tokenLine = showTokenHint
      ? `<div class="text-[10px] text-muted-foreground font-mono text-right">≈ ${bucket.tokenPrice < 0.01 ? bucket.tokenPrice.toFixed(6) : bucket.tokenPrice.toFixed(2)} ${taxTokenSymbol}</div>`
      : '';
    tooltip.innerHTML = `
      <div class="flex items-center justify-between gap-4">
        <span class="text-muted-foreground text-[11px]">Price</span>
        <span class="font-mono text-[11px]">${formatUSD(bucket.usdPrice)}</span>
      </div>${tokenLine}
      <div class="flex items-center justify-between gap-4">
        <span class="flex items-center gap-1.5">
          <span class="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
          <span class="text-muted-foreground text-[11px]">Seats</span>
        </span>
        <span class="font-mono text-[11px]">${bucket.count}</span>
      </div>
      <div class="flex items-center justify-between gap-4">
        <span class="text-muted-foreground text-[11px]">Cumulative</span>
        <span class="font-mono text-[11px]">${bucket.cumulative}</span>
      </div>`;
    tooltip.style.display = 'block';

    const rect = container.getBoundingClientRect();
    const px = rect.left + param.point.x;
    const py = rect.top + param.point.y;
    const tw = tooltip.clientWidth;
    const th = tooltip.clientHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = px + 16 + tw > vw ? px - tw - 12 : px + 16;
    const top = py - 20 + th > vh ? py - th - 8 : Math.max(4, py - 20);
    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  }, [buckets, showTokenHint, taxTokenSymbol]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const chart = createChart(container, {
      layout: { background: { type: ColorType.Solid, color: 'transparent' }, textColor: '#888', attributionLogo: false },
      grid: { vertLines: { visible: false }, horzLines: { color: 'rgba(255,255,255,0.03)' } },
      crosshair: {
        mode: CrosshairMode.Magnet,
        vertLine: { color: 'rgba(255,255,255,0.15)', width: 1, style: 3, labelVisible: false },
        horzLine: { visible: false, labelVisible: false },
      },
      rightPriceScale: { visible: false },
      timeScale: { visible: false, borderVisible: false },
      handleScroll: false,
      handleScale: false,
      width: container.clientWidth,
      height: container.clientHeight,
    });

    const series = chart.addSeries(HistogramSeries, {
      color: 'rgba(245, 158, 11, 0.6)',
      priceFormat: { type: 'volume' },
    });

    chartRef.current = chart;
    seriesRef.current = series;

    const ro = new ResizeObserver(() => {
      chart.resize(container.clientWidth, container.clientHeight);
    });
    ro.observe(container);

    chart.subscribeCrosshairMove(handleCrosshairMove);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, [handleCrosshairMove]);

  useEffect(() => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    if (!series || !chart) return;

    if (buckets.length > 0) {
      try {
        const data = buckets.map((b, i) => ({
          time: (i + 1) as unknown as Time,
          value: b.count,
          color: i === 0 ? 'rgba(245, 158, 11, 0.9)' : 'rgba(245, 158, 11, 0.5)',
        }));
        series.setData(data as any);
        chart.timeScale().fitContent();
      } catch (e) {
        console.warn('[ListingsChart] setData failed:', e);
      }
    }
  }, [buckets]);

  if (buckets.length === 0) {
    return (
      <div className="space-y-2">
        <ChartHeader metric={formatUSD(0)} metricLabel="avg. price" color="blue" />
        <div className="flex items-center justify-center h-44 text-muted-foreground text-sm">
          No listings
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <ChartHeader metric={avgPrice > 0 ? formatUSD(avgPrice) : '—'} metricLabel="avg. price" color="blue" />
      <div className="h-44 -mx-1">
        <div ref={containerRef} className="w-full h-full" />
        {/* Portal — see the SeatSalesChart tooltip comment above. */}
        {typeof document !== 'undefined' && createPortal(
          <div
            ref={tooltipRef}
            className="fixed z-[9999] pointer-events-none rounded-lg border bg-card/95 backdrop-blur-sm p-2.5 shadow-lg text-sm space-y-1.5 min-w-[140px]"
            style={{ display: 'none', top: 0, left: 0 }}
          />,
          document.body,
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function ChartHeader({ metric, metricLabel, ranges, range, setRange, color = 'blue' }: {
  metric: string;
  metricLabel: string;
  ranges?: { key: TimeRange; label: string }[];
  range?: TimeRange;
  setRange?: (r: TimeRange) => void;
  color?: 'blue' | 'emerald';
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-end gap-1.5 min-w-0">
        <span className={cn('text-lg font-bold font-mono leading-tight', color === 'emerald' ? 'text-emerald-400' : 'text-foreground')}>
          {metric}
        </span>
        <span className="text-[10px] text-muted-foreground leading-tight mb-px">{metricLabel}</span>
      </div>
      {ranges && range && setRange && <RangePills ranges={ranges} range={range} setRange={setRange} />}
    </div>
  );
}

function RangePills({ ranges, range, setRange }: { ranges: { key: TimeRange; label: string }[]; range: TimeRange; setRange: (r: TimeRange) => void }) {
  return (
    <div className="flex gap-0.5">
      {ranges.map(r => (
        <button
          key={r.key}
          onClick={() => setRange(r.key)}
          className={cn(
            'text-[9px] px-1.5 py-0.5 rounded transition-colors',
            range === r.key
              ? 'bg-muted text-foreground font-medium'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

export const FloorPriceChart = SeatSalesChart;
